import { useState, useEffect } from "react";
import { BiStar, BiX } from "react-icons/bi";
import { BsEye, BsX } from "react-icons/bs";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getRidersPending,
  getRiderStats,
  getRiderById,
  approveRider,
  rejectRider,
  getRecentActivities,
} from "../../redux/thunk";
import { Rider } from "../../types/rider";
import { formatDistanceToNow } from "date-fns";

// Custom Button Component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode;
  variant?: "default" | "ghost" | "destructive" | "secondary";
  size?: "default" | "sm";
  className?: string;
  onClick?: () => void;
  [key: string]: any;
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  };

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Custom Card Components
const Card = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const CardHeader = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>
      {children}
    </div>
  );
};

const CardTitle = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <h3
      className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
    >
      {children}
    </h3>
  );
};

const CardContent = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>;
};

// Custom Badge Component
const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "default" | "secondary" | "destructive";
  className?: string;
}) => {
  const variants = {
    default: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive: "bg-[#EA1919] text-white",
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

export const LogisticsOverview = () => {
  const dispatch = useAppDispatch();
  const {
    pendingRiders,
    riderStats,
    currentRider,
    status,
    approveStatus,
    rejectStatus,
    error,
    recentActivities,
    recentActivitiesStatus,
  } = useAppSelector((state) => state.logistics);

  const [showRiderModal, setShowRiderModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [selectedRider, setSelectedRider] = useState<Rider | null>(null);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(getRiderStats());
    dispatch(getRidersPending());
    dispatch(getRecentActivities({ page: 1, limit: 10 }));
  }, [dispatch]);

  // Refresh pending riders after approve/reject operations
  useEffect(() => {
    if (approveStatus === "succeeded" || rejectStatus === "succeeded") {
      dispatch(getRidersPending());
      dispatch(getRiderStats());
    }
  }, [approveStatus, rejectStatus, dispatch]);

  const handleRiderApproval = (rider: Rider) => {
    setSelectedRider(rider);
    setShowRiderModal(true);
  };

  const handleAccountDetails = (rider: Rider) => {
    setSelectedRider(rider);
    setShowAccountModal(true);
  };

  const handleApproveRider = async (riderId: string) => {
    await dispatch(approveRider(riderId));
    setShowRiderModal(false);
    setSelectedRider(null);
  };

  const handleRejectRider = async (riderId: string) => {
    await dispatch(rejectRider(riderId));
    setShowRiderModal(false);
    setSelectedRider(null);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-16 border-b border-gray-200 bg-white px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
              <p className="text-sm text-gray-500">
                Welcome back! Here's what's happening with your delivery
                operations.
              </p>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 p-6 overflow-auto">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="flex flex-col border-l-4 border-l-green-600">
              <CardHeader className="flex flex-row items-start space-y-0 pb-4">
                <CardTitle className="text-sm font-medium text-gray-500">
                  Active Riders
                </CardTitle>
              </CardHeader>
              <CardContent className="mt-auto">
                <div className="text-2xl font-bold">
                  {status === "loading" ? "..." : riderStats?.activeRiders ?? 0}
                </div>
                <p className="text-xs text-green-600">
                  Riders currently active
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col border-l-4 border-l-yellow-500">
              <CardHeader className="flex flex-row items-start space-y-0 pb-4">
                <CardTitle className="text-sm font-medium text-gray-500">
                  Orders Today
                </CardTitle>
              </CardHeader>
              <CardContent className="mt-auto">
                <div className="text-2xl font-bold">
                  {status === "loading" ? "..." : riderStats?.ordersToday ?? 0}
                </div>
                <p className="text-xs text-yellow-600">
                  Orders processed today
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col border-l-4 border-l-green-600">
              <CardHeader className="flex flex-row items-start space-y-0 pb-4">
                <CardTitle className="text-sm font-medium text-gray-500">
                  Pending Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="mt-auto">
                <div className="text-2xl font-bold">
                  {status === "loading"
                    ? "..."
                    : riderStats?.pendingIssues ?? 0}
                </div>
                <p className="text-xs text-red-600">
                  Issues requiring attention
                </p>
              </CardContent>
            </Card>

            <Card className="flex flex-col border-l-4 border-l-yellow-500">
              <CardHeader className="flex flex-row items-start space-y-0 pb-4">
                <CardTitle className="text-sm font-medium text-gray-500">
                  Revenue Today
                </CardTitle>
              </CardHeader>
              <CardContent className="mt-auto">
                <div className="text-2xl font-bold">
                  ₦
                  {status === "loading"
                    ? "..."
                    : (riderStats?.revenueToday ?? 0).toLocaleString()}
                  .00
                </div>
                <p className="text-xs text-green-600">
                  Revenue generated today
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Activities */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activities</CardTitle>
                  <p className="text-sm text-gray-500">
                    Latest platform events and updates
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {recentActivitiesStatus === "loading" ? (
                    <div className="text-center text-gray-500">Loading...</div>
                  ) : recentActivities.length === 0 ? (
                    <div className="text-center text-gray-500">
                      No recent activities at the moment
                    </div>
                  ) : (
                    recentActivities.map((activity, index) => {
                      // Determine badge variant based on activity type and action
                      let badgeVariant:
                        | "default"
                        | "secondary"
                        | "destructive" = "default";
                      let badgeText = activity.action;
                      let badgeClass = "";

                      if (
                        activity.action === "approve" ||
                        activity.action === "assigned"
                      ) {
                        badgeClass = "bg-green-600 text-white";
                        badgeText =
                          activity.action === "approve"
                            ? "Success"
                            : "Assigned";
                      } else if (activity.action === "reject") {
                        badgeVariant = "destructive";
                        badgeText = "Rejected";
                      } else if (activity.action === "update") {
                        badgeClass = "bg-green-600 text-white";
                        badgeText = "Updated";
                      } else if (activity.action === "Open") {
                        badgeClass = "bg-yellow-600 text-white";
                        badgeText = "Open";
                      }

                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col text-sm">
                              {activity.description}
                              <span className="text-xs text-gray-500">
                                {formatDistanceToNow(
                                  new Date(activity.createdAt),
                                  {
                                    addSuffix: true,
                                  }
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {badgeClass ? (
                              <Badge className={badgeClass}>{badgeText}</Badge>
                            ) : (
                              <Badge variant={badgeVariant}>{badgeText}</Badge>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Top Performers */}
              <div className="mt-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Top Performers</CardTitle>
                    <p className="text-sm text-gray-500">
                      Top Performers This Week
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium">John Sawyer</p>
                              <p className="text-sm text-gray-500">
                                (24 deliveries)
                              </p>
                            </div>
                            <div className="flex items-center gap-1 bg-[#E99F16] text-white px-2 py-1 rounded">
                              <span className="text-sm font-medium">4.5</span>
                              <BiStar className="w-3 h-3 fill-current" />
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-medium">₦23,500.00</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Pending Approval */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pending Approval</CardTitle>
                  <p className="text-sm text-gray-500">
                    Items Requiring your attention
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {status === "loading" ? (
                    <div className="text-center text-gray-500">Loading...</div>
                  ) : pendingRiders.length === 0 ? (
                    <div className="text-center text-gray-500">
                      No pending riders at the moment
                    </div>
                  ) : (
                    pendingRiders.slice(0, 4).map((rider) => (
                      <div key={rider._id} className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="space-y-2">
                            <p className="font-medium">{rider.fullName}</p>
                            <p className="text-sm text-gray-500">
                              New Registration
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(rider.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-xs text-gray-500">
                            Status: {rider.accountStatus}
                          </div>
                        </div>
                        <Button
                          className="w-full bg-green-600 text-white hover:bg-green-700"
                          onClick={() => handleRiderApproval(rider)}
                        >
                          Review
                        </Button>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>

      {/* Rider Approval Modal */}
      {showRiderModal && selectedRider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-green-600">
                  Rider Approval : {selectedRider.fullName}
                </h3>
                <p className="text-sm text-gray-500">
                  Rider Details and Documentation
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRiderModal(false)}
              >
                <BiX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Phone Number:</p>
                  <p className="text-sm text-gray-500">
                    {selectedRider.phoneNumber}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">E-mail Address:</p>
                  <p className="text-sm text-gray-500">{selectedRider.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Role:</p>
                  <p className="text-sm text-gray-500">{selectedRider.role}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Status:</p>
                  <p className="text-sm text-yellow-600">
                    {selectedRider.accountStatus}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Current Balance:</p>
                  <p className="text-sm text-gray-500">
                    ₦{selectedRider.walletInfo.currentBalance.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Approval Status:</p>
                  <p
                    className={`text-sm ${
                      selectedRider.isApproved
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {selectedRider.isApproved ? "Approved" : "Pending"}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Bank Details:</p>
                <div className="space-y-2">
                  {selectedRider.bankDetails.map((bank, index) => (
                    <div
                      key={bank._id}
                      className="flex items-center justify-between p-2 border rounded"
                    >
                      <div className="text-sm">
                        <p className="font-medium">{bank.bankName}</p>
                        <p className="text-gray-600">{bank.accountName}</p>
                        <p className="text-gray-500">{bank.accountNumber}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAccountDetails(selectedRider)}
                      >
                        <BsEye className="w-4 h-4" />
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  className="flex-1 bg-green-600 text-white hover:bg-green-700"
                  onClick={() => handleApproveRider(selectedRider._id)}
                  disabled={approveStatus === "loading"}
                >
                  {approveStatus === "loading" ? "Approving..." : "Approve"}
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleRejectRider(selectedRider._id)}
                  disabled={rejectStatus === "loading"}
                >
                  {rejectStatus === "loading" ? "Rejecting..." : "Reject"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Details Modal */}
      {showAccountModal && selectedRider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-green-600">
                  Account Details
                </h3>
                <p className="text-sm text-gray-500">Rider Account Details</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAccountModal(false)}
              >
                <BsX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {selectedRider.bankDetails.map((bank, index) => (
                <div key={bank._id} className="border-b pb-4 last:border-b-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium">Account Number:</p>
                      <p className="text-sm text-gray-500">
                        {bank.accountNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Account Name:</p>
                      <p className="text-sm text-gray-500">
                        {bank.accountName}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm font-medium">Bank Name:</p>
                    <p className="text-sm text-gray-500">{bank.bankName}</p>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm font-medium">Status:</p>
                    <p
                      className={`text-sm ${
                        bank.inUse ? "text-green-600" : "text-gray-500"
                      }`}
                    >
                      {bank.inUse ? "Active" : "Inactive"}
                    </p>
                  </div>
                </div>
              ))}

              <div className="pt-4">
                <Button
                  className="w-full bg-green-600 text-white hover:bg-green-700"
                  onClick={() => setShowAccountModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
